import { UserRef, WorkflowItem, WorkflowRequest, generatePath } from '@cibo/core'
import { PaginatedFieldsContext } from '@cibo/landmanager'
import { FieldsLayer, StaticMapGL } from '@cibo/mapgl'
import { useAuth, useWindowTitle } from '@cibo/profile'
import { EnrollmentMessageDialog, useCreateWorkflow, useProgramContent } from '@cibo/programs'
import { FriendlyError, ListCard, Markdown, ResponsiveDialog } from '@cibo/ui'
import ChevronLeft from '@mui/icons-material/ChevronLeft'
import ChevronRight from '@mui/icons-material/ChevronRight'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  Collapse,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Fade,
  Grid,
  Stack,
  Typography,
} from '@mui/material'
import bbox from '@turf/bbox'
import { centroid, featureCollection } from '@turf/turf'
import { ProgramOpportunityList, ProgramOpportunityListItem } from 'components'
import { LayoutSection } from 'components/ImpactLayout'
import { NewEnrollmentFieldList } from 'components/NewEnrollmentFieldList'
import { NewEnrollmentUserItem, NewEnrollmentUserList } from 'components/NewEnrollmentUserList'
import ROUTE from 'navigation/types/routes'
import { useSnackbar } from 'notistack'
import { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

export const CreateEnrollment = ({
  personal,
  programId,
}: {
  personal?: boolean
  programId?: string
}) => {
  const { t } = useTranslation('impact/pages/Enrollments/CreateEnrollment')
  const navigate = useNavigate()
  const { userId, userEmail } = useAuth()
  const [enrollment, setEnrollment] = useState<
    Partial<WorkflowRequest> & Partial<Pick<WorkflowItem, 'ownedBy'>>
  >({
    programId,
    ownedBy:
      personal && !!userId
        ? ({
            userId,
            userPrimaryEmail: `${userEmail}`,
          } as UserRef)
        : undefined,
    benchmark: 'enrollment',
  })
  const [step, setStep] = useState(!!enrollment.programId ? (enrollment.ownedBy ? 2 : 1) : 0)
  const program = useProgramContent(enrollment.programId)
  const { selectedRowIds, selectedFields } = useContext(PaginatedFieldsContext)
  const createEnrollment = useCreateWorkflow()
  const { enqueueSnackbar } = useSnackbar()
  const [showEnrollmentMessageDialog, setShowEnrollmentMessageDialog] = useState(false)
  const [workflowId, setWorkflowId] = useState<string>()

  useEffect(() => {
    setEnrollment({
      ...enrollment,
      fields: selectedRowIds as string[],
    })
  }, [selectedRowIds])

  useWindowTitle(
    program.data?.content?.fields.title
      ? t('windowTitleProgram', { name: program.data.content.fields.title })
      : t('windowTitle')
  )

  const handleSelectProgram = ({ existingEnrollment, id }: ProgramOpportunityListItem) => {
    if (existingEnrollment) {
      navigate(generatePath(ROUTE.ENROLLMENT, { id: existingEnrollment.id }))
    } else {
      setEnrollment({
        ...enrollment,
        programId: id,
      })
      setStep(!!enrollment.ownedBy ? 2 : 1)
    }
  }

  const handleSelectUser = ({
    userId,
    email,
    givenName,
    familyName,
    existingEnrollment,
  }: NewEnrollmentUserItem) => {
    if (existingEnrollment) {
      navigate(generatePath(ROUTE.ENROLLMENT, { id: existingEnrollment.id }))
    } else {
      setEnrollment({
        ...enrollment,
        ownedBy: {
          userId,
          givenName,
          familyName,
          userPrimaryEmail: email,
        },
      })
    }
    setStep(2)
  }

  const handleCommitFields = () => {
    if (personal) {
      handleCreateEnrollment(true)()
    } else {
      setStep(3)
    }
  }

  const handleCreateEnrollment = (edit?: boolean) => () => {
    if (enrollment.benchmark && enrollment.fields && enrollment.programId && enrollment.ownedBy) {
      createEnrollment
        .mutateAsync({
          benchmark: enrollment.benchmark,
          doesUpsert: true,
          fields: enrollment.fields,
          programId: enrollment.programId,
          name: `${enrollment.programId} ${enrollment.benchmark} ${enrollment.ownedBy.userPrimaryEmail}`,
          userId: enrollment.ownedBy.userId === userId ? undefined : enrollment.ownedBy.userId,
        })
        .then(({ data: { id } }) => {
          if (edit) {
            enqueueSnackbar(t('createSuccess'), { variant: 'success' })
            navigate(generatePath(ROUTE.ENROLLMENT, { id }))
          } else {
            setWorkflowId(id)
            setShowEnrollmentMessageDialog(true)
          }
        })
        .catch(e => {
          console.log({ e })
        })
    }
  }

  const handleEnrollmentMessageDialogSuccess = () => {
    enqueueSnackbar(t('messageSent'), { variant: 'success' })
    navigate(generatePath(ROUTE.ENROLLMENTS))
  }

  const growerName =
    enrollment.ownedBy?.givenName && enrollment.ownedBy?.familyName
      ? `${enrollment.ownedBy.givenName} ${enrollment.ownedBy.familyName}`
      : enrollment.ownedBy?.userPrimaryEmail

  const enrollmentOverview = (
    <Stack spacing={2} p={1.5}>
      <Stack>
        <Typography variant="caption">{t('program')}</Typography>
        <Typography>{program.data?.content?.fields.title || '--'}</Typography>
      </Stack>
      <Stack>
        <Typography variant="caption">{t('Grower', { ns: 'clientLocalization' })}</Typography>
        <Typography>{growerName || '--'}</Typography>
      </Stack>
      <Stack>
        <Typography variant="caption">{t('fields')}</Typography>
        <Typography>{enrollment.fields?.length || '--'}</Typography>
      </Stack>
    </Stack>
  )

  const features = useMemo(() => {
    const featureList = selectedFields.map(({ geometryDetail }) => geometryDetail?.input.feature)
    return {
      //@ts-ignore - `feature` mismatch?
      bounds: !!featureList.length ? bbox(featureCollection(featureList)) : undefined,
      //@ts-ignore - `feature` mismatch?
      featureCollection: featureCollection([
        ...selectedFields.map(({ geometryDetail }) => geometryDetail?.input.feature),
        ...selectedFields
          .filter(f => !!f.geometry)
          //@ts-ignore - `feature` mismatch?
          .map(({ geometryDetail }) => centroid(geometryDetail?.input.feature.geometry)),
      ]),
    }
  }, [selectedFields])

  return (
    <>
      <LayoutSection autoHeight container>
        <Divider />
        <Typography variant="h5">{t('newEnrollment', { context: step })}</Typography>
        {step < 3 ? (
          <>
            <Typography>{t('step', { context: step })}</Typography>
            <Box>
              <Grid direction={{ xs: 'row-reverse', md: 'row' }} spacing={4} container>
                <Grid xs={12} md={8} item>
                  {step === 0 && (
                    <ListCard subTitle={t('programs')}>
                      <ProgramOpportunityList
                        rulesVersion="v1"
                        personal={personal}
                        persona={'grower'}
                        onRowClick={({ row }) => handleSelectProgram(row)}
                      />
                    </ListCard>
                  )}
                  {step === 1 && enrollment.programId && (
                    <ListCard subTitle={t('Growers', { ns: 'clientLocalization' })}>
                      <NewEnrollmentUserList
                        programId={enrollment.programId}
                        onRowClick={({ row }) => {
                          handleSelectUser(row)
                        }}
                      />
                    </ListCard>
                  )}
                  {step === 2 && !!enrollment.programId && (
                    <ListCard subTitle={t('fields')}>
                      <NewEnrollmentFieldList
                        baseQuery={{
                          owner: enrollment.ownedBy?.userId,
                          availableForWorkflow: { programId: enrollment.programId },
                        }}
                        programId={enrollment.programId}
                        hideBorders
                      />
                    </ListCard>
                  )}
                </Grid>
                <Grid xs={12} md={4} item>
                  <Stack spacing={4}>
                    <ListCard subTitle={t('enrollment')}>{enrollmentOverview}</ListCard>
                    <Collapse in={step === 2}>
                      <Fade in={step === 2}>
                        <Button
                          variant="contained"
                          color="secondary"
                          disabled={!enrollment.fields?.length || step === 3}
                          onClick={handleCommitFields}
                          endIcon={<ChevronRight />}
                          data-testid="enroll-selected-fields"
                        >
                          {t('addSelectedFields')}
                        </Button>
                      </Fade>
                    </Collapse>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </>
        ) : (
          <Box>
            <Grid spacing={4} container>
              <Grid xs={12} md={4} item>
                <Stack spacing={6}>
                  <Markdown>
                    {t('finalizeInstructions', {
                      count: enrollment.fields?.length,
                      growerName,
                    })}
                  </Markdown>
                  <Stack spacing={1}>
                    <Markdown>
                      {t('notifyInstructions', {
                        count: enrollment.fields?.length,
                        growerName,
                      })}
                    </Markdown>
                    <Box>
                      <LoadingButton
                        variant="contained"
                        color="secondary"
                        size="large"
                        onClick={handleCreateEnrollment()}
                        disabled={createEnrollment.isError}
                        loading={createEnrollment.isLoading}
                      >
                        {t('notify')}
                      </LoadingButton>
                    </Box>
                  </Stack>
                  <Stack spacing={1}>
                    <Markdown>
                      {t('editDetailsInstructions', {
                        count: enrollment.fields?.length,
                        growerName,
                      })}
                    </Markdown>
                    <Box>
                      <LoadingButton
                        variant="contained"
                        color="secondary"
                        size="large"
                        endIcon={<ChevronRight />}
                        onClick={handleCreateEnrollment(true)}
                        disabled={createEnrollment.isError}
                        loading={createEnrollment.isLoading}
                      >
                        {t('editFieldDetails')}
                      </LoadingButton>
                    </Box>
                  </Stack>
                </Stack>
              </Grid>
              <Grid xs={12} md={8} item>
                <ListCard subTitle={t('enrollment')}>
                  <Stack direction="row">
                    <Stack flex={1} justifyContent="space-between" maxWidth={300} minHeight={350}>
                      {enrollmentOverview}
                      <Box m={1.5}>
                        <Button
                          variant="outlined"
                          startIcon={<ChevronLeft />}
                          onClick={() => setStep(2)}
                          fullWidth
                          disabled={createEnrollment.isLoading}
                        >
                          {t('addOrRemoveFields')}
                        </Button>
                      </Box>
                    </Stack>
                    {features.bounds && (
                      //@ts-ignore
                      <StaticMapGL bounds={features.bounds} padding={4}>
                        {/* @ts-ignore */}
                        <FieldsLayer featureCollection={features.featureCollection} />
                      </StaticMapGL>
                    )}
                  </Stack>
                </ListCard>
              </Grid>
            </Grid>
          </Box>
        )}
      </LayoutSection>
      <ResponsiveDialog open={createEnrollment.isError} onClose={() => createEnrollment.reset()}>
        <DialogTitle>{t('createEnrollmentErrorTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <FriendlyError message={t('createEnrollmentErrorBody')} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => createEnrollment.reset()}>
            {t('ok')}
          </Button>
        </DialogActions>
      </ResponsiveDialog>

      {!!workflowId && (
        <EnrollmentMessageDialog
          onSuccess={handleEnrollmentMessageDialogSuccess}
          workflow={{ ...enrollment, id: workflowId } as WorkflowItem}
          onCancel={() => {
            enqueueSnackbar(t('createSuccessNoMessage'), { variant: 'info' })
            navigate(generatePath(ROUTE.ENROLLMENTS))
          }}
          open={showEnrollmentMessageDialog}
        />
      )}
    </>
  )
}
